import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderInput, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Checkbox } from '@/components/shadcn/ui/checkbox';
import { Input } from '@/components/shadcn/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import type {
  AggregatedReportFolder,
  AggregatedReportListItem,
} from '@/entities/report';
import { moveAggregatedReportsToFolderMutationOptions } from '@/entities/report';
import { errorHandler } from '@/error-handler';
import { Button } from '@/shared/ui';
import { toast } from '@/stores/pure/ToastStore';

type Props = {
  workspaceId: string;
  currentFolderId?: string;
  folders: AggregatedReportFolder[];
  selectedReports: AggregatedReportListItem[];
  onClear: () => void;
};

export function ReportMoveActionBar({
  workspaceId,
  currentFolderId,
  folders,
  selectedReports,
  onClear,
}: Props) {
  const queryClient = useQueryClient();
  const moveMutation = useMutation(
    moveAggregatedReportsToFolderMutationOptions(queryClient),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filteredFolders = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    if (!normalizedSearch) return folders;

    return folders.filter((folder) =>
      folder.name.toLocaleLowerCase().includes(normalizedSearch),
    );
  }, [folders, search]);

  const moveReports = async (folderId: string | null) => {
    try {
      await moveMutation.mutateAsync({
        workspace_id: workspaceId,
        aggregated_report_ids: selectedReports.map(
          (report) => report.aggregated_report_id,
        ),
        folder_id: folderId,
      });
      setIsOpen(false);
      setSearch('');
      onClear();
      toast('Reports moved', 'success');
    } catch (error) {
      toast(errorHandler(error, 'Failed to move reports'), 'fail');
    }
  };

  return (
    <div className="absolute bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg border bg-background px-3 py-2.5 shadow-lg">
      <Checkbox
        checked
        aria-label="Clear report selection"
        onCheckedChange={onClear}
      />
      <span className="whitespace-nowrap text-sm font-medium">
        {selectedReports.length} selected
      </span>
      <div className="h-4 w-px bg-border" />
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={
                moveMutation.isPending ||
                (!currentFolderId && folders.length === 0)
              }
            >
              <FolderInput className="size-4" />
              Move to
            </Button>
          </span>
        </PopoverTrigger>
        <PopoverContent align="center" side="top" className="w-72 p-2">
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              placeholder="Search folders"
              className="h-8 pl-8 pr-8 text-sm"
            />
            {search ? (
              <button
                type="button"
                aria-label="Clear folder search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                onClick={() => setSearch('')}
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>
          <div className="max-h-60 overflow-y-auto">
            <button
              type="button"
              className="flex w-full items-center rounded-md px-2.5 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!currentFolderId || moveMutation.isPending}
              onClick={() => void moveReports(null)}
            >
              No folder
            </button>
            {filteredFolders.map((folder) => (
              <button
                key={folder.alias}
                type="button"
                className="flex w-full items-center rounded-md px-2.5 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                disabled={
                  folder.alias === currentFolderId || moveMutation.isPending
                }
                onClick={() => void moveReports(folder.alias)}
              >
                {folder.name}
              </button>
            ))}
            {filteredFolders.length === 0 ? (
              <p className="px-2.5 py-3 text-sm text-muted-foreground">
                No folders found
              </p>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
